/** Client-side cascading for global filters — see APIIntegrationFiles/frontend-integration-filter-relationships.md */

import React, { useState } from "react";
import { Select } from "antd";

export const BLANK_TOKEN = "(Blank)";

/** Keys whose options must preserve API response order (not sorted alphabetically). */
export const PRESERVE_ORDER_KEYS = new Set(["monthName", "month"]);

/** Fixed (non-alphabetical) display order for specific filter fields' dropdown values. */
const CUSTOM_OPTION_ORDER = {
  awardType: ["PRIME", "SUB", "JV", "TBD"],
};

/** Filter keys whose dropdown labels use m/d/yyyy (values sent to API stay yyyy-mm-dd). */
export const DATE_FILTER_KEYS = new Set(["popEndDate"]);

/** Filter keys that use a { start, end } date-range shape in the UI and API. */
export const DATE_RANGE_FILTER_KEYS = new Set(["popEndDate"]);

/** Display ISO yyyy-mm-dd as m/d/yyyy in filter UI. */
export function formatFilterDateLabel(iso) {
  if (iso == null || iso === "") return "";
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
}

/**
 * Normalize popEndDate (or legacy multi-select dates) to API shape:
 * `{ start?, end? }` with empty sides omitted. Returns null when unconstrained.
 */
export function resolvePopEndDateForApi(popEndDate) {
  if (!popEndDate) return null;
  if (Array.isArray(popEndDate)) {
    if (isAllFilterSelected(popEndDate) || popEndDate.length === 0) return null;
    const cleaned = stripFilterSentinels(popEndDate).filter(Boolean);
    if (!cleaned.length) return null;
    const sorted = [...cleaned].sort();
    return { start: sorted[0], end: sorted[sorted.length - 1] };
  }
  if (typeof popEndDate === "object") {
    const start = popEndDate.start ? String(popEndDate.start).trim() : "";
    const end = popEndDate.end ? String(popEndDate.end).trim() : "";
    if (!start && !end) return null;
    const out = {};
    if (start) out.start = start;
    if (end) out.end = end;
    return out;
  }
  return null;
}

export function formatFilterOptionLabel(filterKey, raw) {
  if (DATE_FILTER_KEYS.has(filterKey)) return formatFilterDateLabel(raw) || raw;
  return raw;
}

/** Ant Design Select options — formatted labels, raw API values. */
export function toFilterSelectOptions(filterKey, rawOptions = []) {
  return (rawOptions || []).map((o) => ({
    value: o,
    label: formatFilterOptionLabel(filterKey, o),
  }));
}

/** Match typed text against option label and value (e.g. m/d/yyyy and yyyy-mm-dd). */
export function filterSelectSearchOption(input, option) {
  if (!input?.trim()) return true;
  const q = input.trim().toLowerCase();
  const label = String(option?.label ?? option?.children ?? "").toLowerCase();
  const value = String(option?.value ?? "").toLowerCase();
  return label.includes(q) || value.includes(q);
}

/** Shared Ant Design Select search props for global filter dropdowns. */
export const FILTER_SELECT_SEARCH_PROPS = {
  showSearch: true,
  optionFilterProp: "label",
  filterOption: filterSelectSearchOption,
};

/** UI-only token for the multiselect "All" option — never sent to APIs. */
export const FILTER_ALL_VALUE = "__ALL__";

export function stripFilterSentinels(values = []) {
  return (values || []).filter((v) => v !== FILTER_ALL_VALUE);
}

export function isAllFilterSelected(value) {
  return Array.isArray(value) && value.length === 1 && value[0] === FILTER_ALL_VALUE;
}

export function isAllOptionsSelected(selected, optionValues = []) {
  if (!optionValues.length) return false;
  if (!selected?.length) return false;
  if (selected.length < optionValues.length) return false;
  const set = new Set(selected);
  return optionValues.every((v) => set.has(v));
}

/** Collapse a full selection to the All sentinel; leave partial/empty as-is. */
export function normalizeMultiselectFilterValue(value, optionValues = []) {
  if (isAllFilterSelected(value)) return [FILTER_ALL_VALUE];
  const items = stripFilterSentinels(value);
  if (isAllOptionsSelected(items, optionValues)) return [FILTER_ALL_VALUE];
  return items;
}

/**
 * Resolve Ant Design multi-select onChange when an "All" option is present.
 * State: [] = nothing selected, [FILTER_ALL_VALUE] = All selected (omit from API),
 * otherwise specific values.
 */
export function resolveMultiselectFilterChange(prevValue, nextRaw, optionValues = []) {
  const prevIsAll = isAllFilterSelected(prevValue) || isAllOptionsSelected(stripFilterSentinels(prevValue), optionValues);

  const nextList = Array.isArray(nextRaw) ? nextRaw : [];
  const uiHasAll = nextList.includes(FILTER_ALL_VALUE);
  const uiItems = stripFilterSentinels(nextList);

  if (uiHasAll) return prevIsAll ? [] : [FILTER_ALL_VALUE];
  return normalizeMultiselectFilterValue(uiItems, optionValues);
}

/**
 * Resolve clicking the "All" pseudo-option when a search term has narrowed
 * the visible dropdown list. With no active search (`visibleOptionValues`
 * covers every option), this is identical to the old behavior — toggle the
 * compact "everything" sentinel. While searching, "All" only touches the
 * options CURRENTLY MATCHING the search — added to, or removed from,
 * whatever's already selected — never the full universe. Silently
 * selecting hidden, non-matching options on top of a search is exactly the
 * bug this exists to prevent.
 *
 * O(n) over this one filter's own option list on click only (n tops out at
 * a few hundred items for any filter here) — never per keystroke, never a
 * network call, so this cannot introduce latency.
 */
export function resolveAllClick(value, optionValues, visibleOptionValues) {
  const allSelected = isAllFilterSelected(value) || isAllOptionsSelected(stripFilterSentinels(value), optionValues);
  if (visibleOptionValues.length >= optionValues.length) {
    return allSelected ? [] : [FILTER_ALL_VALUE];
  }
  const current = allSelected ? optionValues : stripFilterSentinels(value);
  const currentSet = new Set(current);
  const allVisibleSelected = visibleOptionValues.length > 0 && visibleOptionValues.every((v) => currentSet.has(v));
  const next = allVisibleSelected
    ? current.filter((v) => !visibleOptionValues.includes(v))
    : [...current, ...visibleOptionValues.filter((v) => !currentSet.has(v))];
  return normalizeMultiselectFilterValue(next, optionValues);
}

/**
 * Global-filter Ant Design multi-select with an "All" pseudo-option —
 * drop-in replacement for `<Select {...buildGlobalFilterMultiSelectProps(...)} />`
 * (every report's filter bar, ~21 call sites, all sharing this exact
 * control). Tracks the user's search text as fully-controlled state
 * (`searchValue` + `onSearch` together — see the note further down on why
 * `onSearch` alone isn't enough) purely so the "All" click below can be
 * scoped to what's currently visible instead of the full option universe.
 *
 * Safe as a genuine per-list-item pattern: every call site renders one of
 * these inside a `.map()` over that report's filter config, each with its
 * own stable `key` — React gives each its own component instance and Hook
 * state, exactly the intended use of Hooks inside a list (a plain function
 * called conditionally inside that same loop, by contrast, would violate
 * the Rules of Hooks the moment the visible filter count changed).
 */
export function GlobalMultiSelectFilter({ value, options, onChange, ...rest }) {
  const [searchText, setSearchText] = useState("");
  const opts = options || [];
  const optionValues = opts.map((o) => o.value);
  const visibleOptionValues = searchText.trim()
    ? opts.filter((o) => filterSelectSearchOption(searchText, o)).map((o) => o.value)
    : optionValues;

  const allSelected = isAllFilterSelected(value) || isAllOptionsSelected(stripFilterSentinels(value), optionValues);
  const selected = allSelected ? optionValues : stripFilterSentinels(value);

  return (
    <>
      {/* Scoped to .gmf-dropdown only — this app has many other Ant Design
          Selects outside the global filter system, and this must not touch
          them. Wrapping (instead of truncating with an ellipsis) is only
          safe with an alternating row background: once a long label spans
          two lines, adjacent rows can otherwise run together with nothing
          marking where one option ends and the next begins. Selected/hover
          states are explicitly excluded so the stripe never masks that a
          row is checked or focused. */}
      <style>{`
        .gmf-dropdown .ant-select-item-option-content {
          white-space: normal;
          overflow: visible;
          text-overflow: unset;
          word-break: break-word;
          line-height: 1.35;
        }
        .gmf-dropdown .ant-select-item {
          align-items: flex-start;
          padding-top: 6px;
          padding-bottom: 6px;
        }
        .gmf-dropdown .ant-select-item-option:nth-child(even):not(.ant-select-item-option-selected):not(.ant-select-item-option-active) {
          background: #F5F7FA;
        }
      `}</style>
      <Select
        mode="multiple"
        value={selected.length ? selected : undefined}
        options={opts}
        onChange={(next) =>
          onChange(resolveMultiselectFilterChange(value, next, optionValues))
        }
        // Fully controlled search text (searchValue + onSearch together), not
        // onSearch alone — Ant Design still owns the search box in the
        // latter, and its own reset of it (the "x" clear button, or its
        // internal post-pick autoClearSearchValue) doesn't reliably reach
        // back out to a plain onSearch listener. With a mirrored-but-not-fed-
        // back copy, that left `searchText` stale after the box visually
        // went empty, so `visibleOptionValues` kept pointing at the old
        // narrowed subset — "All" and every later action kept only ever
        // touching those few options, never the full list, even once every
        // option was visible again. Controlling it outright removes the
        // possibility of the two ever disagreeing; it's the same one
        // setState per keystroke either way, so no added latency.
        searchValue={searchText}
        onSearch={setSearchText}
        onClear={() => setSearchText("")}
        placeholder="All"
        allowClear
        maxTagCount={1}
        maxTagPlaceholder={(omitted) => `+${omitted.length}`}
        popupClassName="gmf-dropdown"
        dropdownRender={(menu) => (
          <>
            <div
              className={`ant-select-item ant-select-item-option${allSelected ? " ant-select-item-option-selected" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                // Bypasses Ant Design's own option list, so its
                // autoClearSearchValue never fires for this click the way it
                // would for a normal pick — clear the search explicitly so
                // the box doesn't keep showing a stale query after "All"
                // just resolved it.
                setSearchText("");
                onChange(resolveAllClick(value, optionValues, visibleOptionValues));
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 32,
                padding: "5px 12px",
                cursor: "pointer",
              }}
            >
              <span className="ant-select-item-option-content">All</span>
              {/* Checked ONLY when every option (not just the currently
                  search-matched ones) is selected — while searching, "All"
                  still ADDS/REMOVES just the visible subset on click (see
                  resolveAllClick), but showing it as checked whenever that
                  subset happens to be fully selected would claim more is
                  selected than actually is. */}
              {allSelected && (
                <span className="ant-select-item-option-state" style={{ color: "#1677ff" }}>
                  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                    <path d="M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 00-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z" />
                  </svg>
                </span>
              )}
            </div>
            {menu}
          </>
        )}
        {...rest}
      />
    </>
  );
}

/** Expand an All sentinel to the concrete option list (for API payloads). */
export function expandAllFilterValue(value, optionValues = []) {
  if (!isAllFilterSelected(value)) return value;
  return optionValues.length ? [...optionValues] : value;
}

/** Replace All sentinels in a filter map with each field's full option list. */
export function expandAllFilterSelections(filters, dropdownOptions = {}) {
  if (!filters) return filters;
  const next = { ...filters };
  Object.keys(next).forEach((key) => {
    if (!isAllFilterSelected(next[key])) return;
    const opts = dropdownOptions[key] || [];
    if (opts.length) next[key] = [...opts];
  });
  return next;
}

/** Map one UI filter value onto an API payload object. */
export function assignApiFilter(target, key, value, optionValues = []) {
  if (!target) return target;
  if (DATE_RANGE_FILTER_KEYS.has(key)) {
    const range = resolvePopEndDateForApi(value);
    if (range) target[key] = range;
    return target;
  }
  if (value == null || value === "" || value === "All") return target;
  const expanded = expandAllFilterValue(value, optionValues);
  if (isAllFilterSelected(expanded)) return target;
  if (Array.isArray(expanded)) {
    const cleaned = stripFilterSentinels(expanded);
    if (cleaned.length) target[key] = cleaned;
    return target;
  }
  target[key] = expanded;
  return target;
}

/** Convert UI filter state to an API filters object (empty selection omitted). */
export function toReportApiFilters(filters, keys, dropdownOptions = {}) {
  const out = {};
  if (!filters) return out;
  const list = keys || Object.keys(filters);
  list.forEach((key) => assignApiFilter(out, key, filters[key], dropdownOptions[key] || []));
  return out;
}

/** Data APIs may omit month/quarter; download payloads must include them. */
export function withPeriodFiltersForDownload(sourceFilters, baseFilters = {}) {
  const out = { ...baseFilters };
  const period = toReportApiFilters(sourceFilters, ["monthName", "quarter"]);
  if (period.monthName) out.monthName = period.monthName;
  if (period.quarter) out.quarter = period.quarter;
  return out;
}

export function formatFilterMultiselectSummary(filterKey, value) {
  if (!value || value.length === 0 || isAllFilterSelected(value)) return "All";
  const labels = stripFilterSentinels(value).map((v) => formatFilterOptionLabel(filterKey, v));
  if (!labels.length) return "All";
  return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`;
}

export function formatFilterMultiselectList(filterKey, values) {
  return stripFilterSentinels(values).map((v) => formatFilterOptionLabel(filterKey, v)).join(", ");
}

/** Ascending sort for filter dropdown values (numeric-aware), or a field's fixed custom order when defined. */
export function sortFilterOptions(options = [], key) {
  const customOrder = CUSTOM_OPTION_ORDER[key];
  if (customOrder) {
    const rank = (v) => {
      const idx = customOrder.findIndex(
        (c) => c.toLowerCase() === String(v).trim().toLowerCase(),
      );
      return idx === -1 ? customOrder.length : idx;
    };
    return [...options].sort((a, b) => {
      const diff = rank(a) - rank(b);
      return diff !== 0
        ? diff
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
    });
  }
  return [...options].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }),
  );
}

export function sortDropdownOptionsMap(optionsByKey = {}) {
  const out = {};
  Object.entries(optionsByKey).forEach(([key, options]) => {
    out[key] = Array.isArray(options) && !PRESERVE_ORDER_KEYS.has(key) ? sortFilterOptions(options, key) : options;
  });
  return out;
}

/** Options for one filter field (static config or loaded dropdown map). */
export function getFilterFieldOptions(f, dropdownOptions = {}) {
  const raw = f?.options || dropdownOptions[f?.key] || [];
  return PRESERVE_ORDER_KEYS.has(f?.key) ? raw : sortFilterOptions(raw, f?.key);
}

/** Global filter key → canonical snake_case row field */
export const FILTER_TO_ROW = {
  contractId: "contract_id",
  contractName: "contract_name",
  customerName: "customer_name",
  projectId: "clin",
  clinLvl2: "clin",
  orgId: "org_id",
  contractType: "contract_type",
  sector: "sector",
  bu: "business_unit",
  competitionType: "competition_type",
  awardType: "award_type",
  businessType: "business_type",
  programManager: "program_manager",
  pc: "pc",
};

const BLANK_NORMALIZED_FIELDS = new Set([
  "contract_type",
  "business_unit",
  "competition_type",
  "award_type",
  "sector",
  "business_type",
  "customer_name",
  "org_id",
  "pop_end_date",
  "program_manager",
  "pc",
]);

const ROW_FIELD_ALIASES = {
  contract_id: ["contract_id", "contractId"],
  contract_name: ["contract_name", "contractName"],
  customer_name: ["customer_name", "customerName"],
  clin: ["clin"],
  proj_id: ["proj_id", "projId", "projectId"],
  org_id: ["org_id", "orgId"],
  contract_type: ["contract_type", "contractType"],
  pop_end_date: ["pop_end_date", "popEndDate"],
  sector: ["sector"],
  business_unit: ["business_unit", "businessUnit", "bu"],
  competition_type: ["competition_type", "competitionType"],
  award_type: ["award_type", "awardType"],
  business_type: ["business_type", "businessType"],
  program_manager: ["program_manager", "programManager"],
  pc: ["pc", "PC", "projectController", "project_controller"],
};

export const CASCADE_FILTER_KEYS = Object.keys(FILTER_TO_ROW);

function pickRawField(raw, field) {
  const aliases = ROW_FIELD_ALIASES[field] || [field];
  for (const key of aliases) {
    const v = raw?.[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

function normalizeBlankField(field, value) {
  if (!BLANK_NORMALIZED_FIELDS.has(field)) return value;
  return value === "" ? BLANK_TOKEN : value;
}

/** Normalize one API row to canonical snake_case (supports snake or camelCase payloads). */
export function normalizeRelationshipRow(raw) {
  const row = {};
  Object.keys(ROW_FIELD_ALIASES).forEach((field) => {
    row[field] = normalizeBlankField(field, pickRawField(raw, field));
  });
  return row;
}

/** Normalize filter_relationships payload to a flat row array. */
export function normalizeRelationshipRows(data) {
  let rows = data;
  if (data && !Array.isArray(data)) {
    if (Array.isArray(data.data)) rows = data.data;
    else if (Array.isArray(data.rows)) rows = data.rows;
    else rows = [];
  }
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeRelationshipRow).filter((row) => row.contract_id || row.clin || row.proj_id);
}

function cascadeKeysForVisible(visibleKeys) {
  if (!visibleKeys) return CASCADE_FILTER_KEYS;
  return CASCADE_FILTER_KEYS.filter((k) => visibleKeys.includes(k));
}

/**
 * Level-2 ancestor of a CLIN's dot-hierarchy, e.g. "22402.064.011" -> "22402.064";
 * "22402.064" -> "22402.064" (already level 2); null when there are fewer than 2
 * segments (no level-2 ancestor exists). See CLIN_LVL2_FILTER.md — the dot is the
 * only valid hierarchy boundary, so this must split on segments, never match on a
 * shared character prefix (e.g. "22402.0641" is a sibling of "22402.064", not a
 * descendant, even though the raw characters line up).
 */
function clinLevel2Ancestor(clin) {
  const s = String(clin ?? "").trim();
  if (!s) return null;
  const parts = s.split(".");
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : null;
}

/** Values on a row that satisfy a global filter key (projectId checks proj_id + clin;
    clinLvl2 surfaces the row's level-2 ancestor, so an exact match against a selected
    ancestor here is equivalent to the server's "equals or strict dot-hierarchy
    descendant" rule — every row under that branch shares the same level-2 ancestor). */
export function getRowFilterValues(row, key) {
  if (!row) return [];
  if (key === "projectId") {
    const vals = [row.proj_id, row.clin].filter((v) => v != null && String(v).trim() !== "");
    return [...new Set(vals)];
  }
  if (key === "clinLvl2") {
    const anc = clinLevel2Ancestor(row.clin);
    return anc ? [anc] : [];
  }
  const field = FILTER_TO_ROW[key];
  if (!field) return [];
  const v = row[field];
  if (v == null || String(v).trim() === "") return [];
  return [v];
}

function rowMatchesSelection(row, key, selected) {
  const rowVals = getRowFilterValues(row, key);
  if (!rowVals.length) return selected.includes(BLANK_TOKEN) || selected.includes("");
  return selected.some((s) => rowVals.includes(s));
}

/** Keep rows matching every active multiselect on cascade keys */
export function filterRowsBySelections(rows, filters, visibleKeys, excludeKey = null) {
  if (!rows?.length) return [];
  const keys = cascadeKeysForVisible(visibleKeys);
  return rows.filter((row) =>
    keys.every((key) => {
      if (excludeKey && key === excludeKey) return true;
      const selected = filters?.[key];
      if (!Array.isArray(selected) || selected.length === 0 || isAllFilterSelected(selected)) return true;
      return rowMatchesSelection(row, key, stripFilterSentinels(selected));
    }),
  );
}

function distinctRowValues(rows, key) {
  const set = new Set();
  rows.forEach((row) => {
    getRowFilterValues(row, key).forEach((v) => set.add(v));
  });
  return [...set];
}

/** Valid options for one key from surviving relationship rows */
export function getCascadedOptionsForKey(filteredRows, key, selectedValues = null) {
  const cascaded = sortFilterOptions(distinctRowValues(filteredRows, key), key);
  if (!Array.isArray(selectedValues) || selectedValues.length === 0 || isAllFilterSelected(selectedValues)) {
    return cascaded;
  }
  return sortFilterOptions([...new Set([...cascaded, ...stripFilterSentinels(selectedValues)])], key);
}

/** Distinct dropdown values for every relationship-backed filter (no per-field GET APIs). */
export function buildBaseOptionsFromRelationships(rows) {
  if (!rows?.length) return {};
  const out = {};
  CASCADE_FILTER_KEYS.forEach((key) => {
    out[key] = getCascadedOptionsForKey(rows, key);
  });
  return out;
}

export function buildCascadedDropdownOptions(relationshipRows, baseOptions, filters, visibleKeys) {
  const out = { ...baseOptions };
  if (!relationshipRows?.length) return sortDropdownOptionsMap(out);

  const keys = cascadeKeysForVisible(visibleKeys);
  keys.forEach((key) => {
    const filtered = filterRowsBySelections(relationshipRows, filters, visibleKeys, key);
    out[key] = getCascadedOptionsForKey(filtered, key, filters?.[key]);
  });
  return sortDropdownOptionsMap(out);
}

/** Drop selections that no longer match the cascade predicate */
export function pruneInvalidCascadeSelections(filters, relationshipRows, visibleKeys) {
  if (!relationshipRows?.length || !filters) return filters;
  const keys = cascadeKeysForVisible(visibleKeys);
  const filtered = filterRowsBySelections(relationshipRows, filters, visibleKeys);
  const next = { ...filters };
  keys.forEach((key) => {
    const sel = next[key];
    if (!Array.isArray(sel) || sel.length === 0 || isAllFilterSelected(sel)) return;
    const validSet = new Set(getCascadedOptionsForKey(filtered, key));
    next[key] = sel.filter((v) => validSet.has(v));
  });
  return next;
}

export function applyCascadeDraftChange(prev, key, value, relationshipRows, visibleKeys) {
  const next = { ...prev, [key]: value };
  return pruneInvalidCascadeSelections(next, relationshipRows, visibleKeys);
}
